export type BrowserPushSupport = "unsupported" | "insecure" | "default" | "denied" | "granted";

export interface BrowserPushSubscriptionPayload {
  endpoint: string;
  expirationTime: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

declare const __TSM_ALERT_WORKER_URL__: string;

const BROWSER_PUSH_SERVICE_WORKER_PATH = "/tsm-alert-sw.js";
const BROWSER_PUSH_SERVICE_WORKER_OPTIONS = {
  scope: "/",
  updateViaCache: "none" as const,
};

// Vite injects a SHA-256 fingerprint of the final public worker asset. The
// fixed root path retains the minimal root scope; the query is only a cache
// key, so a changed worker body always fetches a new edge object.
export const BROWSER_PUSH_SERVICE_WORKER_URL = __TSM_ALERT_WORKER_URL__;

function hasBrowserPushPrimitives(): boolean {
  return typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && "Notification" in window
    && "PushManager" in window;
}

export function getBrowserPushSupport(): BrowserPushSupport {
  if (!hasBrowserPushPrimitives()) return "unsupported";
  if (!window.isSecureContext) return "insecure";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return "default";
}

function base64UrlToUint8Array(value: string): Uint8Array {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
  const binary = window.atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function browserSubscriptionPayload(subscription: PushSubscription): BrowserPushSubscriptionPayload {
  const value = subscription.toJSON() as {
    endpoint?: string;
    expirationTime?: number | null;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!value.endpoint || !value.keys?.p256dh || !value.keys.auth) {
    throw new Error("Your browser returned an incomplete push subscription.");
  }
  return {
    endpoint: value.endpoint,
    expirationTime: value.expirationTime ?? null,
    keys: { p256dh: value.keys.p256dh, auth: value.keys.auth },
  };
}

function isBrowserPushRegistration(registration: ServiceWorkerRegistration): boolean {
  return [registration.active, registration.waiting, registration.installing].some((worker) => {
    if (!worker?.scriptURL) return false;
    return new URL(worker.scriptURL, window.location.origin).pathname === BROWSER_PUSH_SERVICE_WORKER_PATH;
  });
}

async function existingRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!hasBrowserPushPrimitives() || !window.isSecureContext) return null;
  // Do not borrow another same-origin worker's PushManager. Delivery must be
  // bound to this exact root worker so its minimal push/click behavior applies.
  const registration = await navigator.serviceWorker.getRegistration(BROWSER_PUSH_SERVICE_WORKER_PATH);
  return registration && isBrowserPushRegistration(registration) ? registration : null;
}

// This is intentionally safe only after Alert Center has confirmed an active
// opaque device for the current signed-in owner. It never asks permission or
// creates a subscription; it merely makes an existing root registration fetch
// the current content-hashed worker URL.
export async function refreshKnownBrowserPushWorker(): Promise<boolean> {
  if (getBrowserPushSupport() !== "granted") return false;
  const registration = await existingRegistration();
  if (!registration) return false;
  await navigator.serviceWorker.register(BROWSER_PUSH_SERVICE_WORKER_URL, BROWSER_PUSH_SERVICE_WORKER_OPTIONS);
  return true;
}

export async function getExistingBrowserPushSubscription(): Promise<BrowserPushSubscriptionPayload | null> {
  const registration = await existingRegistration();
  if (!registration) return null;
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? browserSubscriptionPayload(subscription) : null;
}

// This is intentionally called only from the explicit Alert Center control.
// Merely viewing the page never registers a worker, creates a subscription, or
// asks for browser permission.
export async function enableBrowserPushDelivery(publicKey: string): Promise<BrowserPushSubscriptionPayload> {
  const support = getBrowserPushSupport();
  if (support === "unsupported" || support === "insecure") {
    throw new Error("This browser cannot receive secure browser delivery.");
  }
  if (support === "denied") {
    throw new Error("Browser notification permission is blocked. Update your browser settings to continue.");
  }
  if (support === "default") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Browser notification permission was not granted.");
    }
  }

  const registration = await navigator.serviceWorker.register(BROWSER_PUSH_SERVICE_WORKER_URL, BROWSER_PUSH_SERVICE_WORKER_OPTIONS);
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToUint8Array(publicKey),
  });
  return browserSubscriptionPayload(subscription);
}

export async function removeBrowserPushSubscription(): Promise<boolean> {
  const registration = await existingRegistration();
  if (!registration) return false;
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? await subscription.unsubscribe() : false;
}
